import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Card } from "./ui/card";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { Activity, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  checkHealthKitAvailability,
  getHealthKitSettings,
  updateHealthKitSettings,
  initializeHealthKit,
  syncTodayHealthKitData,
  type HealthKitSettings as HealthKitSettingsType,
} from "@/lib/healthkit";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { detectNativeApp, waitForHealthKitSDK } from "@/lib/platform";
import { supabase } from "@/integrations/supabase/client";

interface HealthKitSettingsProps {
  userId: string;
}

const HealthKitSettings = ({ userId }: HealthKitSettingsProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [settings, setSettings] = useState<HealthKitSettingsType>({
    enabled: false,
    sync_exercise_time: false,
    sync_stand_hours: false,
    sync_sleep: false,
    sync_steps: false,
    sync_bmi: false,
    sync_body_mass: false,
    sync_heart_rate: false,
    sync_active_energy: false,
  });
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Check availability and load settings
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);

      console.log('[HealthKit Settings] Detecting native app...');

      // Detect if we're in a native app using the new async method
      const detection = await detectNativeApp();

      if (!detection.isNative) {
        console.log('[HealthKit Settings] Not in native app - skipping');
        setIsAvailable(false);
        setIsLoading(false);
        return;
      }

      console.log('[HealthKit Settings] Native app detected:', detection.platformInfo);
      console.log('[HealthKit Settings] Waiting for HealthKit SDK to load...');

      // Wait for SDK to load (up to 5 seconds)
      const sdkLoaded = await waitForHealthKitSDK(5000, 500);

      if (!sdkLoaded) {
        console.log('[HealthKit Settings] HealthKit SDK did not load in time');
        setIsAvailable(false);
        setIsLoading(false);
        return;
      }

      console.log('[HealthKit Settings] SDK loaded, checking availability...');

      // Check if HealthKit is available
      const available = await checkHealthKitAvailability();
      setIsAvailable(available);

      if (available) {
        console.log('[HealthKit Settings] HealthKit is available, loading settings...');
        // Load existing settings
        const userSettings = await getHealthKitSettings(userId);
        if (userSettings) {
          setSettings({
            enabled: userSettings.enabled,
            sync_exercise_time: userSettings.sync_exercise_time,
            sync_stand_hours: userSettings.sync_stand_hours,
            sync_sleep: userSettings.sync_sleep,
            sync_steps: userSettings.sync_steps,
            sync_bmi: userSettings.sync_bmi || false,
            sync_body_mass: userSettings.sync_body_mass || false,
            sync_heart_rate: userSettings.sync_heart_rate || false,
            sync_active_energy: userSettings.sync_active_energy || false,
          });
        }
      } else {
        console.log('[HealthKit Settings] HealthKit not available on this device');
      }

      setIsLoading(false);
    };

    initialize();
  }, [userId]);

  const handleToggleEnabled = async (enabled: boolean) => {
    setSettings((prev) => ({ ...prev, enabled }));

    if (enabled) {
      // First, save settings with at least one metric enabled to allow initialization
      // This ensures initializeHealthKit has metrics to request permissions for
      const initialSettings: Partial<HealthKitSettingsType> = {
        enabled: true,
        // Enable only Steps and Sleep for initial setup
        sync_steps: true,
        sync_sleep: true,
      };

      const saveSuccess = await updateHealthKitSettings(userId, initialSettings);
      if (!saveSuccess) {
        toast.error(t('notifications.toast.failed_to_save_settings_please_try_again'));
        setSettings((prev) => ({ ...prev, enabled: false }));
        return;
      }

      // Update local state to reflect saved settings
      setSettings((prev) => ({
        ...prev,
        sync_steps: true,
        sync_sleep: true,
      }));

      // Initialize HealthKit and request permissions
      // This will show the Apple permission popup
      const initialized = await initializeHealthKit(userId);
      if (!initialized) {
        toast.error(t('notifications.toast.failed_to_enable_healthkit_please_try_again'));
        setSettings((prev) => ({ ...prev, enabled: false }));
        return;
      }

      // Add HealthKit metrics to metric_preferences so they show in quick edit and dashboard
      await addHealthKitMetricsToPreferences(userId, ['healthkit_steps', 'healthkit_sleep_time']);

      // Show success message - permissions popup was shown
      toast.success(t('notifications.toast.healthkit_enabled_you_can_customize_which_metrics_'));
    } else {
      // Disabling HealthKit
      const success = await updateHealthKitSettings(userId, { enabled: false });
      if (success) {
        // Remove all HealthKit metrics from preferences when disabling
        await removeAllHealthKitMetricsFromPreferences(userId);

        // Invalidate queries to update UI immediately
        queryClient.invalidateQueries({ queryKey: ["metric-preferences"] });
        queryClient.invalidateQueries({ queryKey: ["chart-preferences"] });

        toast.success(t('notifications.toast.healthkit_disabled_and_metrics_removed_from_dashbo'));
      } else {
        toast.error(t('notifications.toast.failed_to_update_settings'));
        setSettings((prev) => ({ ...prev, enabled: true }));
      }
    }
  };

  const handleToggleMetric = async (
    metric: keyof Omit<HealthKitSettingsType, "enabled">,
    value: boolean
  ) => {
    setSettings((prev) => ({ ...prev, [metric]: value }));

    const success = await updateHealthKitSettings(userId, { [metric]: value });
    if (success) {
      toast.success(`${getMetricLabel(metric)} sync ${value ? "enabled" : "disabled"}`);

      // Add/remove metric from preferences
      const metricKeyMap: Record<string, string> = {
        sync_exercise_time: 'healthkit_exercise_time',
        sync_steps: 'healthkit_steps',
        sync_sleep: 'healthkit_sleep_time',
        sync_stand_hours: 'healthkit_stand_hours',
        sync_bmi: 'healthkit_bmi',
        sync_body_mass: 'healthkit_body_mass',
        sync_heart_rate: 'healthkit_heart_rate',
        sync_active_energy: 'healthkit_active_energy',
      };

      const metricKey = metricKeyMap[metric];
      if (metricKey) {
        if (value) {
          await addHealthKitMetricsToPreferences(userId, [metricKey]);
        } else {
          await removeHealthKitMetricFromPreferences(userId, metricKey);
        }
      }

      // Re-initialize to update permissions
      if (settings.enabled && value) {
        await initializeHealthKit(userId);
      }
    } else {
      toast.error(t('notifications.toast.failed_to_update_settings'));
      setSettings((prev) => ({ ...prev, [metric]: !value }));
    }
  };

  // Helper function to add HealthKit metrics to metric preferences
  const addHealthKitMetricsToPreferences = async (userId: string, metricKeys: string[]) => {
    try {
      console.log('[HealthKit UI] Adding metrics to preferences:', metricKeys);

      const { data: prefs, error: fetchError } = await supabase
        .from('metric_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error fetching metric preferences:', fetchError);
        toast.error(`Failed to fetch preferences: ${fetchError.message}`);
        return;
      }

      // HealthKit metrics go in activity_sleep_metrics category
      const currentMetrics = prefs?.activity_sleep_metrics || [];
      const newMetrics = [...currentMetrics];

      metricKeys.forEach(key => {
        if (!newMetrics.includes(key)) {
          newMetrics.push(key);
        }
      });

      console.log('[HealthKit UI] Current metrics:', currentMetrics);
      console.log('[HealthKit UI] New metrics:', newMetrics);

      const { error: updateError } = await supabase
        .from('metric_preferences')
        .upsert({
          user_id: userId,
          activity_sleep_metrics: newMetrics,
        });

      if (updateError) {
        console.error('Error updating metric preferences:', updateError);
        toast.error(`Failed to add to preferences: ${updateError.message}`);
      } else {
        console.log('[HealthKit UI] Successfully added metrics to preferences');
        toast.success(`Added ${metricKeys.length} metric(s) to dashboard`, { duration: 3000 });
      }
    } catch (error) {
      console.error('Error in addHealthKitMetricsToPreferences:', error);
      toast.error(`Error adding to preferences: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Helper function to remove HealthKit metric from preferences
  const removeHealthKitMetricFromPreferences = async (userId: string, metricKey: string) => {
    try {
      const { data: prefs, error: fetchError } = await supabase
        .from('metric_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching metric preferences:', fetchError);
        return;
      }

      const currentMetrics = prefs?.activity_sleep_metrics || [];
      const updatedMetrics = currentMetrics.filter((k: string) => k !== metricKey);

      const { error: updateError } = await supabase
        .from('metric_preferences')
        .upsert({
          user_id: userId,
          activity_sleep_metrics: updatedMetrics,
        });

      if (updateError) {
        console.error('Error updating metric preferences:', updateError);
      }
    } catch (error) {
      console.error('Error in removeHealthKitMetricFromPreferences:', error);
    }
  };

  // Helper function to remove all HealthKit metrics from preferences
  const removeAllHealthKitMetricsFromPreferences = async (userId: string) => {
    try {
      console.log('[HealthKit UI] Removing all HealthKit metrics from preferences');

      const { data: prefs, error: fetchError } = await supabase
        .from('metric_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching metric preferences:', fetchError);
        return;
      }

      // Filter out all HealthKit metrics
      const currentMetrics = prefs?.activity_sleep_metrics || [];
      const healthkitMetricPrefixes = ['healthkit_'];
      const updatedMetrics = currentMetrics.filter((k: string) =>
        !healthkitMetricPrefixes.some(prefix => k.startsWith(prefix))
      );

      console.log('[HealthKit UI] Removed metrics:', currentMetrics.filter((k: string) =>
        healthkitMetricPrefixes.some(prefix => k.startsWith(prefix))
      ));

      const { error: updateError } = await supabase
        .from('metric_preferences')
        .upsert({
          user_id: userId,
          activity_sleep_metrics: updatedMetrics,
        });

      if (updateError) {
        console.error('Error updating metric preferences:', updateError);
      } else {
        console.log('[HealthKit UI] Successfully removed all HealthKit metrics from preferences');
      }
    } catch (error) {
      console.error('Error in removeAllHealthKitMetricsFromPreferences:', error);
    }
  };

  const handleSync = async () => {
    if (!settings.enabled) {
      toast.error(t('notifications.toast.please_enable_healthkit_first'));
      return;
    }

    setIsSyncing(true);

    // Show which metrics are enabled before syncing
    // NOTE: Only include metrics that are actually supported and synced
    // Exercise Time and Stand Hours are disabled (coming soon)
    const enabledMetricNames: string[] = [];
    // if (settings.sync_exercise_time) enabledMetricNames.push('Exercise Time'); // DISABLED - coming soon
    // if (settings.sync_stand_hours) enabledMetricNames.push('Stand Hours'); // DISABLED - coming soon
    if (settings.sync_sleep) enabledMetricNames.push('Sleep');
    if (settings.sync_steps) enabledMetricNames.push('Steps');
    if (settings.sync_bmi) enabledMetricNames.push('BMI');
    if (settings.sync_body_mass) enabledMetricNames.push('Body Mass');
    if (settings.sync_heart_rate) enabledMetricNames.push('Heart Rate');
    if (settings.sync_active_energy) enabledMetricNames.push('Active Energy');

    console.log('[HealthKit UI] Syncing with these enabled metrics:', enabledMetricNames);

    // Show info toast with enabled metrics
    const infoToastId = toast.info(`Syncing: ${enabledMetricNames.join(', ')}`, {
      duration: 10000, // Keep visible during sync
    });

    try {
      const success = await syncTodayHealthKitData(userId);

      // Check for detailed sync results stored by the sync function
      const syncResults = (window as any).__healthKitLastSyncResults as Array<{
        metric: string;
        success: boolean;
        error?: string;
        value?: any;
      }> || [];

      if (success) {
        // After successful sync, ensure enabled metrics are in preferences
        // NOTE: Only include metrics that are actually supported and synced
        // Exercise Time and Stand Hours are disabled (coming soon)
        const enabledMetrics: string[] = [];
        // if (settings.sync_exercise_time) enabledMetrics.push('healthkit_exercise_time'); // DISABLED - coming soon
        if (settings.sync_steps) enabledMetrics.push('healthkit_steps');
        if (settings.sync_sleep) enabledMetrics.push('healthkit_sleep_time');
        // if (settings.sync_stand_hours) enabledMetrics.push('healthkit_stand_hours'); // DISABLED - coming soon
        if (settings.sync_bmi) enabledMetrics.push('healthkit_bmi');
        if (settings.sync_body_mass) enabledMetrics.push('healthkit_body_mass');
        if (settings.sync_heart_rate) enabledMetrics.push('healthkit_heart_rate');
        if (settings.sync_active_energy) enabledMetrics.push('healthkit_active_energy');

        if (enabledMetrics.length > 0) {
          await addHealthKitMetricsToPreferences(userId, enabledMetrics);
        }

        // Show detailed results
        const failedMetrics = syncResults.filter(r => !r.success);
        const successMetrics = syncResults.filter(r => r.success);

        if (failedMetrics.length > 0) {
          const failedNames = failedMetrics.map(m => `${m.metric}: ${m.error}`).join(', ');
          if (successMetrics.length > 0) {
            const successDetails = successMetrics.map(m => {
              const daysInfo = m.daysWithData ? `${m.daysWithData} days` : '';
              return `${m.metric}: ${daysInfo}`;
            }).join(', ');
            toast.warning(`Synced: ${successDetails}. Failed: ${failedNames}`, { duration: 8000 });
          } else {
            toast.error(`All metrics failed: ${failedNames}`);
          }
        } else {
          // Show the actual values that were synced
          const successDetails = successMetrics.map(m => {
            const value = m.value;
            const daysInfo = m.daysWithData && m.daysWithData > 1 ? ` (${m.daysWithData} days synced)` : '';
            
            // Format the value based on the metric type
            if (m.metric === 'Steps') {
              if (typeof value === 'number') return `${m.metric}: ${value.toLocaleString()} steps today${daysInfo}`;
              return `${m.metric}: ${m.daysWithData || 0} days synced`;
            }
            if (m.metric === 'Exercise Time') return `${m.metric}: ${value} min${daysInfo}`;
            if (m.metric === 'Sleep Time') {
              if (typeof value === 'number') return `${m.metric}: ${value.toFixed(1)} hrs today${daysInfo}`;
              return `${m.metric}: ${m.daysWithData || 0} days synced`;
            }
            if (m.metric === 'Stand Hours') return `${m.metric}: ${value} hrs${daysInfo}`;
            if (m.metric === 'Active Energy') return `${m.metric}: ${value} kcal${daysInfo}`;
            return `${m.metric}: ${m.daysWithData || 0} days synced`;
          }).join(', ');
          toast.success(`HealthKit synced: ${successDetails}`, { duration: 8000 });

          // Show debug info if Steps returned NaN
          const stepsDebug = (window as any).__healthKitStepsDebug;
          if (stepsDebug && (isNaN(stepsDebug.result) || stepsDebug.result === null || stepsDebug.result === undefined)) {
            toast.info(`Debug - HealthKit returned: result=${stepsDebug.result}, type=${stepsDebug.type}, response=${JSON.stringify(stepsDebug.response)}`, { duration: 15000 });
          }
        }

        // Invalidate queries to refresh the UI with new data
        queryClient.invalidateQueries({ queryKey: ["daily-entry"] });
        queryClient.invalidateQueries({ queryKey: ["daily-entries"] });
        queryClient.invalidateQueries({ queryKey: ["detailed-entries"] });
        queryClient.invalidateQueries({ queryKey: ["healthkit-data"] });

        setLastSyncTime(new Date());
      } else {
        // Show which metrics failed
        const syncError = (window as any).__healthKitSyncError;
        const failedMetrics = syncResults.filter(r => !r.success);

        let errorMessage = "Failed to sync HealthKit data";

        if (syncError) {
          errorMessage = `Sync Error: ${syncError}`;
        } else if (failedMetrics.length > 0) {
          const details = failedMetrics.map(m => `${m.metric}: ${m.error}`).join(', ');
          errorMessage = `Failed metrics: ${details}`;
        }

        toast.error(errorMessage, { duration: 10000 });
      }
    } catch (error) {
      console.error("[HealthKit UI] Sync error:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(`Sync crashed: ${errorMessage}`, { duration: 10000 });
    } finally {
      setIsSyncing(false);
    }
  };

  // Verification function to check what data was actually saved
  const verifyDataSaved = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      // Check daily_entries for today
      const { data: entry, error } = await supabase
        .from('daily_entries')
        .select('healthkit_steps, healthkit_exercise_time, healthkit_sleep_time, healthkit_stand_hours')
        .eq('user_id', userId)
        .eq('entry_date', today)
        .maybeSingle();

      if (error) {
        toast.error(`Failed to verify data: ${error.message}`);
        return;
      }

      if (entry) {
        const savedMetrics = [];
        if (entry.healthkit_steps !== null) savedMetrics.push(`Steps: ${entry.healthkit_steps}`);
        if (entry.healthkit_exercise_time !== null) savedMetrics.push(`Exercise: ${entry.healthkit_exercise_time}min`);
        if (entry.healthkit_sleep_time !== null) savedMetrics.push(`Sleep: ${entry.healthkit_sleep_time}hrs`);
        if (entry.healthkit_stand_hours !== null) savedMetrics.push(`Stand: ${entry.healthkit_stand_hours}hrs`);

        if (savedMetrics.length > 0) {
          toast.info(`DB saved: ${savedMetrics.join(', ')}`, { duration: 8000 });
        } else {
          toast.warning(t('notifications.toast.no_data_saved_today'), { duration: 5000 });
        }
      } else {
        toast.warning(t('notifications.toast.no_daily_entry_today'), { duration: 5000 });
      }
    } catch (error) {
      console.error('Error verifying data:', error);
    }
  };

  const getMetricLabel = (metric: string): string => {
    const labels: Record<string, string> = {
      sync_exercise_time: t('settings.healthkit.metrics.exercise_time'),
      sync_stand_hours: t('settings.healthkit.metrics.stand_hours'),
      sync_sleep: t('settings.healthkit.metrics.sleep_time'),
      sync_steps: t('settings.healthkit.metrics.steps'),
      sync_bmi: t('settings.healthkit.metrics.bmi'),
      sync_body_mass: t('settings.healthkit.metrics.body_mass'),
      sync_heart_rate: t('settings.healthkit.metrics.heart_rate'),
      sync_active_energy: t('settings.healthkit.metrics.active_energy'),
    };
    return labels[metric] || metric;
  };

  const getMetricEmoji = (metric: string): string => {
    const emojis: Record<string, string> = {
      sync_exercise_time: "🏋️",
      sync_stand_hours: "🚶",
      sync_sleep: "😴",
      sync_steps: "👟",
      sync_bmi: "📏",
      sync_body_mass: "⚖️",
      sync_heart_rate: "❤️",
      sync_active_energy: "🔥",
    };
    return emojis[metric] || "📊";
  };

  if (isLoading) {
    return (
      <Card className="p-6 mt-4">
        <div className="flex items-center gap-3 mb-4">
          <Activity className="w-5 h-5" />
          <h3 className="text-lg font-semibold">{t('settings.healthkit.title')}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t('settings.loading')}</p>
      </Card>
    );
  }

  if (!isAvailable) {
    return (
      <Card className="p-6 mt-4">
        <div className="flex items-center gap-3 mb-4">
          <Activity className="w-5 h-5" />
          <h3 className="text-lg font-semibold">{t('settings.healthkit.title')}</h3>
        </div>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('settings.healthkit.not_available')}</AlertTitle>
          <AlertDescription>
            {t('settings.healthkit.not_available_desc')}
          </AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <Card className="p-6 mt-4">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5" />
            <h3 className="text-lg font-semibold">{t('settings.healthkit.title')}</h3>
          </div>
          {settings.enabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={isSyncing}
            >
              {isSyncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {t('settings.healthkit.syncing')}
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('settings.healthkit.sync_now')}
                </>
              )}
            </Button>
          )}
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground">
          {t('settings.healthkit.description')}
        </p>

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between py-2 border-b">
          <div className="space-y-1">
            <Label htmlFor="healthkit-enabled" className="font-semibold">
              {t('settings.healthkit.enable')}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t('settings.healthkit.sync_on_startup')}
            </p>
          </div>
          <Switch
            id="healthkit-enabled"
            checked={settings.enabled}
            onCheckedChange={handleToggleEnabled}
          />
        </div>

        {/* Individual Metric Toggles */}
        {settings.enabled && (
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">{t('settings.healthkit.select_metrics')}</h4>

            {/* Steps */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <span className="text-xl">{getMetricEmoji("sync_steps")}</span>
                <div>
                  <Label htmlFor="sync-steps">{getMetricLabel("sync_steps")}</Label>
                  <p className="text-xs text-muted-foreground">{t('ui.text.daily_step_count')}</p>
                </div>
              </div>
              <Switch
                id="sync-steps"
                checked={settings.sync_steps}
                onCheckedChange={(value) => handleToggleMetric("sync_steps", value)}
              />
            </div>

            {/* Sleep Time */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <span className="text-xl">{getMetricEmoji("sync_sleep")}</span>
                <div>
                  <Label htmlFor="sync-sleep">{getMetricLabel("sync_sleep")}</Label>
                  <p className="text-xs text-muted-foreground">{t('ui.text.total_sleep_hours')}</p>
                </div>
              </div>
              <Switch
                id="sync-sleep"
                checked={settings.sync_sleep}
                onCheckedChange={(value) => handleToggleMetric("sync_sleep", value)}
              />
            </div>

            {/* Exercise Time - Coming Soon */}
            <div className="flex items-center justify-between py-2 opacity-50">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xl flex-shrink-0">{getMetricEmoji("sync_exercise_time")}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="sync-exercise" className="text-muted-foreground whitespace-nowrap">
                      {getMetricLabel("sync_exercise_time")}
                    </Label>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border whitespace-nowrap flex-shrink-0">
                      coming soon
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('ui.text.daily_exercise_minutes')}</p>
                </div>
              </div>
              <Switch
                id="sync-exercise"
                checked={false}
                disabled
                className="flex-shrink-0"
              />
            </div>

            {/* Stand Hours - Coming Soon */}
            <div className="flex items-center justify-between py-2 opacity-50">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xl flex-shrink-0">{getMetricEmoji("sync_stand_hours")}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="sync-stand" className="text-muted-foreground whitespace-nowrap">
                      {getMetricLabel("sync_stand_hours")}
                    </Label>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border whitespace-nowrap flex-shrink-0">
                      coming soon
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('ui.text.hours_standing_per_day')}</p>
                </div>
              </div>
              <Switch
                id="sync-stand"
                checked={false}
                disabled
                className="flex-shrink-0"
              />
            </div>

            {/* BMI - Coming Soon */}
            <div className="flex items-center justify-between py-2 opacity-50">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xl flex-shrink-0">{getMetricEmoji("sync_bmi")}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="sync-bmi" className="text-muted-foreground whitespace-nowrap">
                      {getMetricLabel("sync_bmi")}
                    </Label>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border whitespace-nowrap flex-shrink-0">
                      coming soon
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('ui.text.body_mass_index')}</p>
                </div>
              </div>
              <Switch
                id="sync-bmi"
                checked={false}
                disabled
                className="flex-shrink-0"
              />
            </div>

            {/* Body Mass - Coming Soon */}
            <div className="flex items-center justify-between py-2 opacity-50">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xl flex-shrink-0">{getMetricEmoji("sync_body_mass")}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="sync-body-mass" className="text-muted-foreground whitespace-nowrap">
                      {getMetricLabel("sync_body_mass")}
                    </Label>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border whitespace-nowrap flex-shrink-0">
                      coming soon
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('ui.text.body_weight')}</p>
                </div>
              </div>
              <Switch
                id="sync-body-mass"
                checked={false}
                disabled
                className="flex-shrink-0"
              />
            </div>

            {/* Heart Rate - Coming Soon */}
            <div className="flex items-center justify-between py-2 opacity-50">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xl flex-shrink-0">{getMetricEmoji("sync_heart_rate")}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="sync-heart-rate" className="text-muted-foreground whitespace-nowrap">
                      {getMetricLabel("sync_heart_rate")}
                    </Label>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border whitespace-nowrap flex-shrink-0">
                      coming soon
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('ui.text.average_heart_rate')}</p>
                </div>
              </div>
              <Switch
                id="sync-heart-rate"
                checked={false}
                disabled
                className="flex-shrink-0"
              />
            </div>

            {/* Active Energy - Coming Soon */}
            <div className="flex items-center justify-between py-2 opacity-50">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xl flex-shrink-0">{getMetricEmoji("sync_active_energy")}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="sync-active-energy" className="text-muted-foreground whitespace-nowrap">
                      {getMetricLabel("sync_active_energy")}
                    </Label>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border whitespace-nowrap flex-shrink-0">
                      coming soon
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('ui.text.active_calories_burned')}</p>
                </div>
              </div>
              <Switch
                id="sync-active-energy"
                checked={false}
                disabled
                className="flex-shrink-0"
              />
            </div>
          </div>
        )}

        {/* Last Sync Info */}
        {settings.enabled && lastSyncTime && (
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Last synced: {lastSyncTime.toLocaleString()}
            </AlertDescription>
          </Alert>
        )}

        {/* Privacy Notice */}
        {settings.enabled && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle className="text-xs">{t('ui.text.privacy_sharing')}</AlertTitle>
            <AlertDescription className="text-xs">
              HealthKit data is synced to your profile and can be viewed by friends and on
              public profiles if your profile is public. You can disable individual metrics
              at any time.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </Card>
  );
};

export default HealthKitSettings;
